import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import {
  useGetCartQuery,
  useManageCartItem,
  useRemoveCartItem,
} from "@/api/AppApi/services/cart";
import {
  CartItemTypes,
  ICartItemType,
  IGetCart,
} from "@/api/AppApi/services/cart/interfaces/cart-interfaces";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import {
  ICommonProductDetails,
  ProductPreviewTypes,
  unit,
} from "@/api/AppApi/services/product/interfaces/api-product-details-interface";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import {
  debounce,
  getById_ItemPropsToPost_Product_Event,
  getCartId,
} from "@/app/components/pages-components/cart/cart-functions";
import { PagesPaths } from "@/utils/functions/pages-paths";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState, useRef } from "react";
import { isSignedInAtom } from "../authAtoms";
import {
  compareAddOnsArrays,
  getProductFromCart,
  getSelectedAddOnsPrice,
  isRequiredAddonsNotChoosed,
} from "./product-has-attrs-helpers/functions";
import {
  IAddOnWithQuantity,
  IAddons,
  ICartItemExtraFields,
  ISelectedBundleItems,
  IuseProductHasAttrsResult,
} from "./product-has-attrs-helpers/interfaces";

import { intersectionsArray } from "@/app/components/common-addons-builder/addon-functions";

import useProductPopup from "../useProductPopup";
import { currencyAtom } from "../currencyAtom";
import useEventTrackers from "../../utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import { EventType } from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/helpers/interfaces";
import calculatPricesForSpecialPrices from "@/utils/functions/calculatPriceForSpecialPrices";
import { usePathname, useSearchParams } from "next/navigation";
import useSellingMode from "../useSellingMode";
import { IEventCustomData } from "@/api/AppApi/services/home/IEvent";
import { isKWCompliance } from "@/utils/functions/global-constants";
import {
  groupBundleItemsByBundleItemId,
  compareBundleItems,
} from "@/app/components/pages-components/product-details-page/bundle/functions";

export const useProductHasAttrs = (
  product: ICommonProductDetails,
): IuseProductHasAttrsResult => {
  const getComapny = useGetCompanyQuery();
  const { enableSells } = useSellingMode();
  const show_all_product_units =
    getComapny.data?.result.theme.components.product.show_all_product_units;

  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();

  const [isSignedIn] = useAtom(isSignedInAtom);
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;

  const [addedToCart, setAddedToCart] = useState(false);
  const [note, setNote] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState<number>();
  const selectedUnit = selectedUnitId
    ? product.units.find((item) => item.id === selectedUnitId)
    : null;
  const [quantity, setQuantity] = useState(
    selectedUnit?.min_quantity_in_cart ?? product.min_quantity_in_cart ?? 1,
  );
  const [selectedAddOns, setSelectedAddOns] = useState<IAddons | undefined>({});
  const [selectedAddOnsPrice, setSelectedAddOnsPrice] = useState(0);
  const [selectedChildIndex, setSelectedChildIndex] = useState<number | null>(
    null,
  );
  //other_inputs
  const [otherInputs, setOtherInputs] = useState<object>({});

  const [selectedBundleItems, setSelectedBundleItems] = useState<
    ISelectedBundleItems[]
  >([]);

  const [currency] = useAtom(currencyAtom);

  // Ref to store the timeout for debouncing addon changes
  const addonChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { eventTrackers } = useEventTrackers();

  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const { handleClosePopup, popupValue } = useProductPopup();

  const { cartItemID } = popupValue ?? {};

  const cart_item_referer = searchParams.get("cart_item_referer");

  const addOns: IAddOnWithQuantity[][] = [];
  for (let key in selectedAddOns) {
    selectedAddOns[key] && addOns.push(selectedAddOns[key]);
  }

  const currentProd =
    selectedChildIndex === null || selectedChildIndex === -1
      ? product
      : product?.children[selectedChildIndex];

  const cartItemFromAddOnsAndBundle = (cartItems ?? [])?.find((item) => {
    const basicMatch =
      item.inner_id === currentProd?.id &&
      compareAddOnsArrays(item.addons, addOns.flat());

    // If it's a product type, also check bundle items
    if (item.type === CartItemTypes.product) {
      const currentBundleItems =
        groupBundleItemsByBundleItemId(selectedBundleItems);
      const itemBundleItems = item.bundle_items || [];

      return (
        basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
      );
    }

    return basicMatch;
  });

  const cartItemId =
    cartItemID !== null && cartItemID !== undefined
      ? cartItemID
      : cart_item_referer !== null && cart_item_referer !== undefined
      ? +cart_item_referer
      : cartItemFromAddOnsAndBundle?.id;
  const disablechangeAddon =
    (!!cartItemID || !!cart_item_referer) && addedToCart;

  const currentProductInCart = getProductFromCart(
    cartItems ?? [],
    currentProd,
    show_all_product_units!,
    cartItemId,
  );
  const resetProduct = (justResetAddedTocart?: boolean) => {
    setAddedToCart(currentProductInCart.addedToCart);
    if (justResetAddedTocart) return;
    setSelectedAddOns(currentProductInCart.selectedAddons);
    setNote(currentProductInCart.note);
    setQuantity(currentProductInCart.quantity);
    setOtherInputs(currentProductInCart.other_inputs);
    setSelectedUnitId(currentProductInCart.unit_id);
    setSelectedBundleItems(currentProductInCart.bundle_items);
  };

  const manageCartApi = useManageCartItem({
    onSuccess: (res: IGetCart) => {
      const { items, coupon } = res.result ?? {};
      if (res?.message?.type === ApiMsgTypes.success) {
        if (product?.preview_type === ProductPreviewTypes.consultation) {
          !!popupValue?.productID && handleClosePopup();
          router.push(
            isSignedIn ? PagesPaths.userCheckout : PagesPaths.checkout,
          );
        }
        setAddedToCart(true);
        const addedItem = items.find(
          (item) => item.inner_id === currentProd.id,
        )!;
        eventTrackers(EventType.ADD_CART, [addedItem], {
          currency: currency?.code,
          value: addedItem?.price,
          coupon: coupon?.code ?? "",
          discount: addedItem?.discount,
        });
      }
    },
    onError: (error) => {
      resetProduct();
      // console.log("error", error.response.data.message);
      setNotFieldErr([error?.response?.data?.message?.text]);
    },
  });

  const removeItemApi = useRemoveCartItem({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        setAddedToCart(false);
      }
      if (!!cart_item_referer) {
        router.push(pathName);
      }
    },
    onError: (_error) => {
      resetProduct();
      // console.log(error);
    },
  });

  useEffect(() => {
    if (getCart.isFetching) return;
    resetProduct(!cartItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCart?.isFetching, currentProd?.id, cartItems, cartItemId]);
  useEffect(() => {
    setQuantity(currentProductInCart.quantity);
    setSelectedUnitId(currentProductInCart.unit_id);
  }, [currentProductInCart.unit_id, currentProductInCart.quantity]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addonChangeTimeoutRef.current) {
        clearTimeout(addonChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleAddToCart = (
    extraFields: ICartItemExtraFields,
    other_inputs: any = {},
    redirectToCheckOutOnSuccess?: boolean,
  ) => {
    const itemType = () => {
      switch (extraFields.type) {
        case CartItemTypes.slot:
          return {
            type: extraFields.type,
            date: extraFields.date,
            product_id: extraFields.product_id,
          };
        case CartItemTypes.consultation:
          return {
            type: extraFields.type,
            message: extraFields.message,
            files: extraFields.files,
          };
        default:
          return {
            type: extraFields.type,
            addons: addOns.flat(),
          };
      }
    };
    const basicFields = {
      cartId: undefined,
      id: currentProd?.id,
      quantity: quantity,
      note: note,
      attrs_values: intersectionsArray(
        product.attribute_ids,
        currentProd.attribute_ids,
      ), // currentProd.attribute_ids,
      unit_id: selectedUnitId,
    };
    manageCartApi.mutate(
      {
        ...basicFields,
        ...itemType(),
        ...other_inputs,
        bundle_items: groupBundleItemsByBundleItemId(selectedBundleItems),
      },
      {
        onSuccess: () => {
          const KWC = isKWCompliance(getComapny.data?.result!);
          if (KWC || redirectToCheckOutOnSuccess) {
            router.push(
              isSignedIn ? PagesPaths.userCheckout : PagesPaths.checkout,
            );
            return;
          }
        },
      },
    );
  };

  const mutateQty = (
    qty: number,
    id: number,
    unit_id: number,
    cartItems: ICartItemType[],
  ) => {
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      id,
      currentProd.preview_type,
      cartItemId,
    );
    manageCartApi.mutate({
      ...cartItem,
      quantity: qty,
      unit_id,
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceMutate = useCallback(debounce(mutateQty, 500), [cartItemId]);
  const handleQuantityChange = (qty: number) => {
    if (!addedToCart) {
      setQuantity(qty > 0 ? qty : 1);
      return;
    }
    if (qty === 0) {
      const itemId =
        getCartId(cartItems ?? [], currentProd?.id, cartItemId) ?? 0;
      const item = cartItems?.find((item) => item.id === itemId)!;
      removeItemApi.mutate(itemId);
      eventTrackers(EventType.remove_from_cart, [item], {
        discount: item.discount,
        tax: item.tax,
        value: item.price,
        currency: currency?.code,
      });
      return;
    }
    setQuantity(qty);
    debounceMutate(qty, currentProd?.id, selectedUnitId, cartItems);
  };

  const handleSelectedAddOnsChanged = (currentAddons: any) => {
    setSelectedAddOns(currentAddons);
    const addOns: IAddOnWithQuantity[] = [];
    for (let key in currentAddons) {
      currentAddons[key] && addOns.push(currentAddons[key]);
    }

    const cartItemFromAddOnsAndBundle = (cartItems ?? [])?.find((item) => {
      const basicMatch =
        item.inner_id === currentProd?.id &&
        compareAddOnsArrays(item.addons, addOns.flat());

      // If it's a product type, also check bundle items
      if (item.type === CartItemTypes.product) {
        const currentBundleItems =
          groupBundleItemsByBundleItemId(selectedBundleItems);
        const itemBundleItems = item.bundle_items || [];

        return (
          basicMatch && compareBundleItems(currentBundleItems, itemBundleItems)
        );
      }

      return basicMatch;
    });

    const cartItemId =
      cartItemID !== null && cartItemID !== undefined
        ? cartItemID
        : cart_item_referer !== null && cart_item_referer !== undefined
        ? +cart_item_referer
        : cartItemFromAddOnsAndBundle?.id;

    setSelectedAddOnsPrice(
      getSelectedAddOnsPrice(currentProd.addons_builder, addOns),
    );
    if (!addedToCart || !cartItemId) {
      return;
    }

    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      currentProd.id,
      currentProd.preview_type,
      cartItemId,
    );
    const addOnesChanged = !compareAddOnsArrays(
      cartItem?.addons,
      addOns.flat(),
      true,
    );
    if (addOnesChanged) {
      // Clear any existing timeout
      if (addonChangeTimeoutRef.current) {
        clearTimeout(addonChangeTimeoutRef.current);
      }

      // Set new timeout
      addonChangeTimeoutRef.current = setTimeout(() => {
        console.log("go");
        addonChangeTimeoutRef.current = null;
        manageCartApi.mutate({ ...cartItem, addons: addOns.flat() });
      }, 1000);
    }
  };

  const handleNoteChange = (newNote: string) => {
    setNote(newNote);
    if (!addedToCart) return;
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      currentProd.id,
      currentProd.preview_type,
      cartItemId,
    );
    manageCartApi.mutate({ ...cartItem, note: newNote });
  };

  const getUnitPrice =
    (
      showUnit: boolean,
      units: unit[],
      selectedUnitId: number | undefined,
      product: ICommonProductDetails,
    ) =>
    (priceType: "price" | "price_without_discount" | "discount_amount" | "discount_type" | "discount") => {
      if (!showUnit || !units.length) {
        return calculatPricesForSpecialPrices({
          originalPrice: product.price,
          originalPriceWithoutDiscount: product.price_without_discount,
          originalDiscountAmount: product.discount_amount,
          originalDiscountType: product.discount_type,
          originalDiscount: product.discount
        })({ quantity, specialPrices: product.selling_prices })[priceType];
      } else {
        const selectedUnit = units.find(({ id }) => id === selectedUnitId)!;
        return calculatPricesForSpecialPrices({
          originalPrice: selectedUnit?.price,
          originalPriceWithoutDiscount: selectedUnit?.price_without_discount,
          originalDiscountAmount: selectedUnit?.discount_amount,
          originalDiscountType: selectedUnit?.discount_type,
          originalDiscount: selectedUnit?.discount,
          unitId: selectedUnitId,
        })({ quantity, specialPrices: product.selling_prices })[priceType];
      }
    };

  const getPrise = getUnitPrice(
    show_all_product_units as boolean,
    currentProd.units,
    selectedUnitId,
    currentProd,
  );

  const subTotalPrice = !cartItemId
    ? (getPrise("price") as number) + selectedAddOnsPrice
    : currentProductInCart?.price ?? 0;
  const shouldConfig =
    (currentProd?.is_configurable && selectedChildIndex === null) ||
    currentProd?.has_configurable_parent;
  const variantNotExist = selectedChildIndex === -1;
  const requiredAddonsNotChosen = isRequiredAddonsNotChoosed(
    currentProd?.addons_builder,
    selectedAddOns ?? {},
  );

  const currentProductQuantityInCart =
    cartItems
      ?.filter((item) => item.inner_id === currentProd.id)
      .filter((item) => item.id !== cartItemId)
      .reduce((prev, current) => prev + current.quantity, 0) ?? 0;

  const notAllBundleItemsChildrenSelected =
    selectedBundleItems.filter((item) => item.is_configurable && !item.child)
      .length > 0;

  const outOfStock =
    currentProd?.quantity_on_hand - currentProductQuantityInCart <= 0 ||
    (!!currentProd.min_quantity_in_cart &&
      currentProd.quantity_on_hand - currentProductQuantityInCart <
        currentProd.min_quantity_in_cart);

  const disableRegistrationEvent =
    currentProd.preview_type === ProductPreviewTypes.event &&
    !(currentProd.custom_data as IEventCustomData).enable_registration;

  const cartDisabled =
    shouldConfig ||
    variantNotExist ||
    requiredAddonsNotChosen ||
    (outOfStock && !addedToCart) ||
    disableRegistrationEvent ||
    notAllBundleItemsChildrenSelected ||
    !enableSells;
  const plusDisabled =
    quantity >=
    Math.min(
      currentProd?.quantity_on_hand,
      selectedUnit?.max_quantity_in_cart ??
        currentProd?.max_quantity_in_cart ??
        999,
    ) -
      currentProductQuantityInCart;

  const handleSelectUnits = (unit: unit) => {
    setSelectedUnitId(unit.id);
    if (unit?.min_quantity_in_cart && !addedToCart)
      setQuantity(unit?.min_quantity_in_cart);
    const cartItem = getById_ItemPropsToPost_Product_Event(
      cartItems ?? [],
      currentProd.id,
      currentProd.preview_type,
      cartItemId,
    );
    if (addedToCart) {
      let quantity =
        unit?.min_quantity_in_cart &&
        cartItem.quantity < unit?.min_quantity_in_cart
          ? unit?.min_quantity_in_cart
          : cartItem.quantity;
      quantity =
        unit?.max_quantity_in_cart &&
        cartItem.quantity > unit?.max_quantity_in_cart
          ? unit?.max_quantity_in_cart
          : cartItem.quantity;
      manageCartApi.mutate({ ...cartItem, quantity, unit_id: unit.id });
    }
  };

  return {
    currentProd,
    selectedChildIndex,
    quantity,
    note,
    selectedAddOns: selectedAddOns ?? {},
    selectedAddOnsPrice,
    addedToCart,
    setSelectedChildIndex,
    handleAddToCart,
    handleNoteChange,
    handleQuantityChange,
    handleSelectedAddOnsChanged,
    subTotalPrice,
    plusDisabled,
    shouldConfig,
    variantNotExist,
    requiredAddonsNotChosen,
    outOfStock,
    cartDisabled,
    otherInputs,
    notFieldErr,
    setNotFieldErr,
    handleSelectUnits,
    selectedUnitId,
    selectedUnit,
    getPrise,
    currency,
    cartItemId,
    disablechangeAddon,
    disableRegistrationEvent,
    selectedBundleItems,
    setSelectedBundleItems,
    notAllBundleItemsChildrenSelected,
  };
};
